import { debugLogger } from '@/utils/debugLogger';

interface CachedImage {
  blob: Blob;
  url: string;
  timestamp: number;
  etag?: string;
  lastModified?: string;
}

interface ImageCacheStats {
  totalSize: number;
  itemCount: number;
  hitRate: number;
  totalRequests: number;
  cacheHits: number;
}

class ImageCacheService {
  private cache = new Map<string, CachedImage>();
  private maxCacheSize = 50 * 1024 * 1024; // 50MB
  private maxAge = 24 * 60 * 60 * 1000; // 24 hours
  private stats: ImageCacheStats = {
    totalSize: 0,
    itemCount: 0,
    hitRate: 0,
    totalRequests: 0,
    cacheHits: 0
  };

  /**
   * Get cached image or fetch and cache if not available
   */
  async getImage(url: string, forceRefresh: boolean = false): Promise<string> {
    this.stats.totalRequests++;

    const cacheKey = this.getCacheKey(url);
    const cached = this.cache.get(cacheKey);

    // Check if cached image is valid
    if (cached && !forceRefresh && this.isCacheValid(cached)) {
      debugLogger.debug('[ImageCache] Cache hit for:', url);
      this.stats.cacheHits++;
      this.updateHitRate();
      return cached.url;
    }

    try {
      debugLogger.debug('[ImageCache] Fetching image:', url);
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      // Get cache headers
      const etag = response.headers.get('etag');
      const lastModified = response.headers.get('last-modified');

      const cachedImage: CachedImage = {
        blob,
        url: objectUrl,
        timestamp: Date.now(),
        etag: etag || undefined,
        lastModified: lastModified || undefined
      };

      // Clean up old cached URL if exists
      if (cached) {
        URL.revokeObjectURL(cached.url);
      }

      // Add to cache and manage size
      this.cache.set(cacheKey, cachedImage);
      this.updateCacheStats();
      this.evictIfNeeded();

      this.updateHitRate();
      return objectUrl;
    } catch (error) {
      debugLogger.error('[ImageCache] Error fetching image:', error);
      
      // Return cached image if available, even if expired
      if (cached) {
        debugLogger.debug('[ImageCache] Using expired cache as fallback');
        return cached.url;
      }
      
      throw error;
    }
  }

  /**
   * Preload images for better UX
   */
  async preloadImages(urls: string[]): Promise<void> {
    const promises = urls.map(url => 
      this.getImage(url).catch(error => {
        debugLogger.warn('[ImageCache] Preload failed for:', url, error);
        return null;
      })
    );

    await Promise.allSettled(promises);
  }

  /**
   * Check if image needs refresh based on headers
   */
  async shouldRefresh(url: string): Promise<boolean> {
    const cacheKey = this.getCacheKey(url);
    const cached = this.cache.get(cacheKey);

    if (!cached || !cached.etag) {
      return true;
    }

    try {
      const response = await fetch(url, { method: 'HEAD' });
      const currentEtag = response.headers.get('etag');
      
      return currentEtag !== cached.etag;
    } catch {
      return false; // Keep cached version if HEAD request fails
    }
  }

  /**
   * Clear expired cache entries
   */
  clearExpired(): void {
    const now = Date.now();
    let removedCount = 0;

    for (const [key, cached] of this.cache.entries()) {
      if (now - cached.timestamp > this.maxAge) {
        URL.revokeObjectURL(cached.url);
        this.cache.delete(key);
        removedCount++;
      }
    }

    debugLogger.debug(`[ImageCache] Removed ${removedCount} expired entries`);
    this.updateCacheStats();
  }

  /**
   * Clear all cache
   */
  clearAll(): void {
    for (const cached of this.cache.values()) {
      URL.revokeObjectURL(cached.url);
    }
    this.cache.clear();
    this.updateCacheStats();
    this.resetStats();
    debugLogger.debug('[ImageCache] All cache cleared');
  }

  /**
   * Get cache statistics
   */
  getStats(): ImageCacheStats {
    return { ...this.stats };
  }

  /**
   * Get cache size in MB
   */
  getCacheSizeMB(): number {
    return this.stats.totalSize / (1024 * 1024);
  }

  private getCacheKey(url: string): string {
    return url.split('?')[0]; // Remove query params for consistent caching
  }

  private isCacheValid(cached: CachedImage): boolean {
    const age = Date.now() - cached.timestamp;
    return age < this.maxAge;
  }

  private async evictIfNeeded(): Promise<void> {
    if (this.stats.totalSize <= this.maxCacheSize) {
      return;
    }

    // Sort by timestamp (oldest first)
    const entries = Array.from(this.cache.entries())
      .sort(([, a], [, b]) => a.timestamp - b.timestamp);

    // Remove oldest entries until under limit
    let removedSize = 0;
    for (const [key, cached] of entries) {
      if (this.stats.totalSize - removedSize <= this.maxCacheSize * 0.8) {
        break;
      }

      URL.revokeObjectURL(cached.url);
      this.cache.delete(key);
      removedSize += cached.blob.size;
    }

    debugLogger.debug(`[ImageCache] Evicted ${removedSize / 1024 / 1024}MB from cache`);
    this.updateCacheStats();
  }

  private updateCacheStats(): void {
    this.stats.totalSize = Array.from(this.cache.values())
      .reduce((sum, cached) => sum + cached.blob.size, 0);
    this.stats.itemCount = this.cache.size;
  }

  private updateHitRate(): void {
    this.stats.hitRate = this.stats.totalRequests > 0 
      ? (this.stats.cacheHits / this.stats.totalRequests) * 100 
      : 0;
  }

  private resetStats(): void {
    this.stats = {
      totalSize: 0,
      itemCount: 0,
      hitRate: 0,
      totalRequests: 0,
      cacheHits: 0
    };
  }
}

export const imageCacheService = new ImageCacheService();

// Auto cleanup and periodic tasks are disabled during tests to avoid open handles
const IS_TEST =
  (typeof process !== 'undefined' && (process.env.VITEST === 'true' || process.env.NODE_ENV === 'test')) ||
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.MODE === 'test') ||
  (typeof window !== 'undefined' && (window as any).__VITEST_BROWSER__ === true);

if (!IS_TEST && typeof window !== 'undefined') {
  // Auto cleanup on page unload
  window.addEventListener('beforeunload', () => {
    imageCacheService.clearAll();
  });

  // Cleanup expired entries every 5 minutes
  setInterval(() => {
    imageCacheService.clearExpired();
  }, 5 * 60 * 1000);
}
